import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { User, EcosystemProfileSetting } from '@frontend/types';
import { authClient } from '@frontend/api-clients/authClient';
import { fetchProfileEcosystems, updateProfileEcosystem } from '@frontend/api-clients/profileEcosystemsClient';

interface EcosystemSettingsPageProps {
  user: User;
}


export default function EcosystemSettingsPage({ user }: EcosystemSettingsPageProps) {
  const [ecosystems, setEcosystems] = useState<EcosystemProfileSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const loadProfiles = async () => {
      setLoading(true);
      setError('');
      setSuccess('');

      try {
        const token = authClient.getToken();
        if (!token) {
          setError('You must be signed in to view ecosystem settings.');
          setLoading(false);
          return;
        }

        const list = await fetchProfileEcosystems(token);
        setEcosystems(list);
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Failed to load ecosystem settings');
      } finally {
        setLoading(false);
      }
    };

    loadProfiles();
  }, []);

  const updateField = (ecosystemId: string, field: 'displayName' | 'replyEmail', value: string) => {
    setEcosystems((current) =>
      current.map((ecosystem) =>
        ecosystem.id === ecosystemId
          ? {
              ...ecosystem,
              [field]: value
            }
          : ecosystem
      )
    );
  };

  const saveEcosystemSettings = async (event: FormEvent, ecosystem: EcosystemProfileSetting) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    setSavingId(ecosystem.id);

    try {
      const token = authClient.getToken();
      if (!token) {
        throw new Error('You must be signed in to save ecosystem settings.');
      }

      const data = await updateProfileEcosystem(ecosystem.id, token, {
        displayName: ecosystem.displayName,
        replyEmail: ecosystem.replyEmail,
      });

      setEcosystems((current) =>
        current.map((entry) => (entry.id === ecosystem.id ? (data as EcosystemProfileSetting) : entry))
      );
      setSuccess(`Saved settings for ${ecosystem.name}.`);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to save ecosystem settings');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="dashboard-page">
      <nav className="navbar">
        <div className="navbar-brand">Ecosystem Settings</div>
        <div className="navbar-menu">
          <span className="dashboard-welcome">Signed in as {user.name || user.username}</span>
          <Link to="/dashboard" className="btn btn-secondary">
            Back to Dashboard
          </Link>
        </div>
      </nav>

      <div className="container dashboard-container">
        <div className="dashboard-hero">
          <h1 className="dashboard-title">Identity By Ecosystem</h1>
          <p className="dashboard-subtitle">
            Manage the display name and reply email shown to members of each ecosystem.
          </p>
        </div>

        {error && <div className="settings-message settings-error">{error}</div>}
        {success && <div className="settings-message settings-success">{success}</div>}

        {loading ? (
          <div className="ecosystem-card settings-card">Loading ecosystem settings...</div>
        ) : ecosystems.length === 0 ? (
          <div className="ecosystem-card settings-card">
            No ecosystem memberships were found for your account.
          </div>
        ) : (
          <div className="grid grid-2">
            {ecosystems.map((ecosystem) => (
              <form
                key={ecosystem.id}
                className="ecosystem-card settings-card"
                onSubmit={(event) => saveEcosystemSettings(event, ecosystem)}
              >
                <h2 className="ecosystem-title">{ecosystem.name}</h2>
                <p className="ecosystem-description">
                  Set profile fields visible within this ecosystem.
                </p>

                <label className="settings-label" htmlFor={`${ecosystem.id}-display`}>
                  Display Name
                </label>
                <input
                  id={`${ecosystem.id}-display`}
                  type="text"
                  value={ecosystem.displayName}
                  maxLength={120}
                  onChange={(event) => updateField(ecosystem.id, 'displayName', event.target.value)}
                />
                {ecosystem.isDisplayNameFallback && (
                  <p className="settings-hint">Using your global profile value.</p>
                )}

                <label className="settings-label" htmlFor={`${ecosystem.id}-reply`}>
                  Reply Email
                </label>
                <input
                  id={`${ecosystem.id}-reply`}
                  type="email"
                  value={ecosystem.replyEmail}
                  maxLength={254}
                  onChange={(event) => updateField(ecosystem.id, 'replyEmail', event.target.value)}
                />
                {ecosystem.isReplyEmailFallback && (
                  <p className="settings-hint">Using your global profile value.</p>
                )}

                <div className="settings-actions">
                  <button type="submit" className="btn btn-primary" disabled={savingId === ecosystem.id}>
                    {savingId === ecosystem.id ? 'Saving...' : 'Save'}
                  </button>
                  {ecosystem.updatedAt && (
                    <span className="settings-updated">
                      Updated {new Date(ecosystem.updatedAt).toLocaleString()}
                    </span>
                  )}
                </div>
              </form>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
